import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ClassesEntity } from '../../../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { UserEntityService } from '../../../../../../services/settings/user-entity.service';
import { RightsCases } from '../../../../../../models/rights-cases-enum';
import { ExportService } from '../../../../../../services/export.service';

const SUB_TAB = {
  USER_VMS: 0,
  TEMPLATES: 1,
  USER_ACCOUNTS: 2,
} as const;

@Component({
  selector: 'app-resources-tab',
  templateUrl: './resources-tab.component.html',
  styleUrls: ['./resources-tab.component.css'],
})
export class ResourcesTabComponent implements OnInit, OnDestroy {
  @Input() classId!: string;
  @Input() classEntity!: ClassesEntity;
  @Input() hasVmResource = false;
  @Input() hasAccResource = false;

  /** Emitted after a template VM action (create/destroy/etc.) so the parent can silently
   * reload classEntity — keeps the Settings > VM Template tab's provisioning state in sync
   * without requiring a manual page refresh. */
  @Output() templateVmChanged = new EventEmitter<void>();

  readonly SUB_TAB = SUB_TAB;
  selectedSubTabIndex: number = SUB_TAB.USER_VMS;

  /** Resolved once from RightsCases.deleteResource — gates Destroy actions on all sub-tabs. */
  canDestroyResources = false;

  /** True after the first load so lazy-load guard prevents redundant API calls on tab re-focus. */
  resourcesLoaded = false;

  // --- User VM data ---
  userVms: any[] = [];
  isLoadingUserVms = false;
  userVmsError: string | null = null;

  // --- Template VM data ---
  templateVms: any[] = [];
  isLoadingTemplateVms = false;
  templateVmsError: string | null = null;

  // --- User account data ---
  userAccounts: any[] = [];
  isLoadingUserAccounts = false;
  userAccountsError: string | null = null;

  // --- Cost analysis data ---
  costingData: any = null;
  isLoadingCosting = false;
  costingError: string | null = null;

  // --- VM power states (userId → powerState string) ---
  vmPowerStates: Record<string, string> = {};
  isLoadingPowerStates = false;

  private readonly destroy$ = new Subject<void>();
  private readonly userId = localStorage.getItem('userId') ?? '';

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly userEntityService: UserEntityService,
    readonly exportService: ExportService,
  ) { }

  ngOnInit(): void {
    this.checkDestroyRights();
    this.loadResources();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubTabChange(index: number): void {
    this.selectedSubTabIndex = index;
  }

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  private loadResources(): void {
    if (this.resourcesLoaded) return;
    this.resourcesLoaded = true;

    this.loadUserVms();
  }

  loadUserVms(): void {
    this.isLoadingUserVms = true;
    this.userVmsError = null;
    this.classService.getClassResources(this.classId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.userVms = data?.userVMs ?? [];
          this.templateVms = data?.templates ?? [];
          this.userAccounts = data?.userAccounts ?? [];
          this.isLoadingUserVms = false;
          this.isLoadingTemplateVms = false;
          this.isLoadingUserAccounts = false;
          this.loadVmPowerStates();
        },
        error: (err: any) => {
          const msg = err?.error?.message ?? 'Failed to load class resources.';
          this.userVmsError = msg;
          this.templateVmsError = msg;
          this.userAccountsError = msg;
          this.isLoadingUserVms = false;
          this.isLoadingTemplateVms = false;
          this.isLoadingUserAccounts = false;
        },
      });
  }

  loadVmPowerStates(): void {
    this.isLoadingPowerStates = true;
    this.classService.getVMPowerStates(this.classId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (states) => {
          this.vmPowerStates = states ?? {};
          this.isLoadingPowerStates = false;
        },
        error: () => {
          this.isLoadingPowerStates = false;
        },
      });
  }

  loadCostingData(): void {
    if (this.isLoadingCosting) return;
    this.isLoadingCosting = true;
    this.costingError = null;
    this.classService.getCostingData(this.classId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.costingData = data;
          this.isLoadingCosting = false;
        },
        error: (err: any) => {
          this.costingError = err?.error?.message ?? 'Failed to load cost data.';
          this.isLoadingCosting = false;
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Rights
  // ---------------------------------------------------------------------------

  private checkDestroyRights(): void {
    if (!this.userId) return;
    this.userEntityService
      .rightsCasesGetByUserId(this.userId, [RightsCases.deleteResource])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rights: any) => {
          this.canDestroyResources = rights?.deleteResource === true;
        },
        error: () => {
          this.canDestroyResources = false;
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  redactAccountPasswords(account: any): any {
    if (!account) return account;
    const redacted = { ...account };
    if ('password' in redacted) redacted['password'] = '[REDACTED]';
    if ('Password' in redacted) redacted['Password'] = '[REDACTED]';
    return redacted;
  }
}
